import { memo, useCallback, useEffect, useState } from 'react';
import filesize from 'filesize';
import LoadingIndicator from 'components/ui/LoadingIndicator';
import { useSelectableItemsContext } from 'components/ui/widget/contexts/selectableItems';
import { Ellipsis } from '@/components/ui/ellipsis/ellipsis';
import { downloadFile } from 'utils/downloadFile';
import { formatDateWithoutSeconds } from 'utils';
import { getByPath } from 'utils/widgets';
import { FileIcon } from '@/components/fileIcon/fileIcon';
import { cn } from '@/shadcn/lib/utils';
import { FileView } from '@/components/ui/widget/views/file/file.view.type';
import { WidgetData } from '@/components/ui/widget/widget.type';

const DEFAULT_FILE_PATHS = {
  USER_NAME: 'upload.user',
  DATE: 'upload.date'
};

type Props = {
  view: FileView;
  data: WidgetData;
  setSelectableItemData?: (data: unknown) => void;
};

export const FileViewImpl = memo(
  ({ view, data, setSelectableItemData = undefined }: Props) => {
    const selectableItemsContext = useSelectableItemsContext();

    const [loading, setLoading] = useState(false);
    const [downloadResponse, setDownloadResponse] = useState(null);

    const {
      fileDataPath = '',
      uploadUserNamePath = DEFAULT_FILE_PATHS.USER_NAME,
      uploadDatePath = DEFAULT_FILE_PATHS.DATE
    } = view;

    const fileData = getByPath(data, fileDataPath);
    const uploadUserName = getByPath(data, uploadUserNamePath);
    const uploadDate = getByPath(data, uploadDatePath);

    useEffect(() => {
      if (setSelectableItemData) setSelectableItemData(fileData);
    }, [fileData]);

    const downloadFileFn = useCallback(
      () => downloadFile(fileData, setLoading, loading, setDownloadResponse),
      [fileData]
    );

    if (!fileData || typeof fileData !== 'object') return null;

    return (
      <div className="relative">
        <LoadingIndicator
          spinning={loading}
          showSuccess={
            downloadResponse !== null || !!selectableItemsContext?.isCompleted
          }
        >
          <div
            className={cn(
              'text-accent-foreground bg-accent border-border hover:text-primary flex cursor-pointer flex-row flex-nowrap items-center justify-between gap-2.5 overflow-hidden rounded-md border p-2.5 whitespace-nowrap',
              loading && 'opacity-50'
            )}
            onClick={downloadFileFn}
            data-cy="file-link"
          >
            <div className="flex grow flex-row flex-nowrap items-center gap-2.5 overflow-hidden">
              <FileIcon name={fileData.name} className="w-5" />
              <div className="">
                <Ellipsis className="overflow-hidden text-xs">
                  {fileData.name}
                </Ellipsis>
                <div className="text-muted-foreground/80 text-[10px]">
                  {filesize(fileData.size)}
                </div>
              </div>
            </div>
            <div className="flex flex-col flex-nowrap text-[10px]">
              {uploadUserName && <div>{uploadUserName}</div>}
              {uploadDate && (
                <div className="text-muted-foreground/80">
                  {formatDateWithoutSeconds(uploadDate)}
                </div>
              )}
            </div>
          </div>
        </LoadingIndicator>
      </div>
    );
  }
);

// sort defaults to text on datePath (text is ok assuming iso dates)
const getSortConfig = view => ({
  type: 'text',
  path: view.datePath || DEFAULT_FILE_PATHS.DATE
});

export const fileViewProvider = {
  component: FileViewImpl,
  getSortConfig
};
